/**
 * Marktergebnis zu einem Lauf melden — ohne Umweg über Claude.
 *
 * WARUM HIER: Die Trefferquote ist die einzige Zahl, die sagt, ob die Jury
 * recht hat, und sie entsteht nur aus gemeldeten Ergebnissen. Wochen nach dem
 * Lauf sitzt niemand mehr in derselben Unterhaltung mit Claude; die Zahlen
 * stehen im Ads- oder Analytics-Konto, und diese Seite liegt daneben.
 *
 * DIESELBEN REGELN WIE IN CLAUDE: Die Route ruft `meldeErgebnis` aus
 * `trefferquote.ts`, dieselbe Funktion wie `report_outcome`. Was nicht zählt
 * (unentschiedener Lauf, unentschiedener Markt, Unterschied im Rauschen, kein
 * Test mit fester Aufteilung), sagt der Server samt Grund — die Seite zeigt es
 * nur an.
 *
 * WARUM DER TESTAUFBAU PFLICHT IST (#311, 22.10.2026): In die Trefferquote
 * kommen nur Tests mit fester Aufteilung. Ohne Angabe gälte „unbekannt“, und
 * die Meldung zählte nicht; die Seite fragt deshalb, statt still zu raten.
 *
 * WARUM DIE ROHZAHLEN SO WEIT OBEN STEHEN: Ohne Zähler und Nenner lässt sich
 * nicht prüfen, ob der Unterschied im Markt überhaupt einer war. Sie sind
 * freiwillig, aber der Hinweis sagt, was ohne sie fehlt.
 */

import { useState, type FormEvent } from 'react';
import { meldeErgebnis, type ErgebnisMeldung, type Lauf } from '../api';
import { Hinweis, KleinerKnopf, Knopf } from '../ui';
import { fassungsname } from '../fassungsname';
import { sprache, t } from '../sprache';

const METRIKEN = [
  { wert: 'anfragen', text: 'Anfragen je Besuch (Conversion Rate)', ereignis: 'Anfragen', basis: 'Besuche' },
  { wert: 'ctr', text: 'Klickrate', ereignis: 'Klicks', basis: 'Impressionen' },
  { wert: 'kaeufe', text: 'Käufe je Besuch', ereignis: 'Käufe', basis: 'Besuche' },
  { wert: 'sonstiges', text: 'Etwas anderes', ereignis: 'Ereignisse', basis: 'Grundgesamtheit' },
] as const;

/** Englische Fassung der Texte, je `wert`. Gewählt beim Rendern (sprache.ts, Kopfkommentar). */
const METRIKEN_EN: Record<(typeof METRIKEN)[number]['wert'], { text: string; ereignis: string; basis: string }> = {
  anfragen: { text: 'Inquiries per visit (conversion rate)', ereignis: 'Inquiries', basis: 'Visits' },
  ctr: { text: 'Click-through rate', ereignis: 'Clicks', basis: 'Impressions' },
  kaeufe: { text: 'Purchases per visit', ereignis: 'Purchases', basis: 'Visits' },
  sonstiges: { text: 'Something else', ereignis: 'Events', basis: 'Population' },
};

const metrikText = (x: (typeof METRIKEN)[number]) => (sprache() === 'en' ? METRIKEN_EN[x.wert] : x);

const AUFBAUTEN = [
  { wert: 'experiment', text: 'Google-Ads-Experiment mit fester Aufteilung', en: 'Google Ads experiment with a fixed split' },
  { wert: 'split_test', text: 'Split-Test (Landingpage oder E-Mail, zufällig aufgeteilt)', en: 'Split test (landing page or email, randomly split)' },
  { wert: 'rsa_assets', text: 'Werte einzelner Assets in einer RSA', en: 'Figures for single assets within an RSA' },
  { wert: 'vorher_nachher', text: 'Erst die eine, dann die andere Fassung', en: 'One version first, then the other' },
  { wert: 'unbekannt', text: 'Weiß ich nicht', en: "I don't know" },
] as const;

const zahl = (v: string): number | undefined => {
  // Englisch: Komma trennt Tausender, Punkt die Dezimalen.
  const n = Number(sprache() === 'en' ? v.replace(/[,\s]/g, '') : v.replace(/\./g, '').replace(',', '.'));
  return v.trim() && Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
};

export default function ErgebnisMelden({ lauf, fertig, abbrechen }: {
  lauf: Lauf;
  fertig: (antwort: { grund: string; zaehlbar: boolean; quote: string }) => void;
  abbrechen: () => void;
}) {
  const [gewinner, setGewinner] = useState<ErgebnisMeldung['gewinner'] | ''>('');
  const [aufbau, setAufbau] = useState<ErgebnisMeldung['testaufbau'] | ''>('');
  const [metrik, setMetrik] = useState<(typeof METRIKEN)[number]['wert']>('anfragen');
  const [z, setZ] = useState({ kz: '', kn: '', bz: '', bn: '' });
  const [zeitraum, setZeitraum] = useState('');
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(false);
  const m = metrikText(METRIKEN.find(x => x.wert === metrik)!);

  const zahlen = { kandidat_zaehler: zahl(z.kz), kandidat_nenner: zahl(z.kn), baseline_zaehler: zahl(z.bz), baseline_nenner: zahl(z.bn) };
  const vollstaendig = Object.values(zahlen).every(v => v !== undefined);
  const teilweise = !vollstaendig && Object.values(zahlen).some(v => v !== undefined);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    if (!gewinner) { setFehler(t('Wähle, welche Fassung im Markt besser lief.', 'Choose which version did better in the market.')); return; }
    if (!aufbau) { setFehler(t('Wähle, wie getestet wurde.', 'Choose how it was tested.')); return; }
    if (teilweise) { setFehler(t('Die Zahlen bitte ganz oder gar nicht — mit nur einem Teil lässt sich nichts prüfen.', 'Please enter all the numbers or none. With only some of them, nothing can be checked.')); return; }
    if (vollstaendig && (zahlen.kandidat_zaehler! > zahlen.kandidat_nenner! || zahlen.baseline_zaehler! > zahlen.baseline_nenner!)) {
      setFehler(t(`${m.ereignis} können nicht mehr sein als ${m.basis}.`, `${m.ereignis} cannot be more than ${m.basis.toLowerCase()}.`));
      return;
    }
    setLaedt(true);
    try {
      const a = await meldeErgebnis({
        run: lauf.id, gewinner, testaufbau: aufbau, metrik,
        ...(vollstaendig ? zahlen : {}),
        ...(zeitraum.trim() ? { zeitraum: zeitraum.trim() } : {}),
      });
      fertig({ grund: a.grund, zaehlbar: a.zaehlbar, quote: a.trefferquote.satz });
    } catch (err) {
      setFehler((err as Error).message);
    } finally {
      setLaedt(false);
    }
  }

  const eingabe = 'w-full box-border rounded-lg border border-linie bg-flaeche px-3 py-2 text-[0.9375rem] tabular-nums text-text outline-none focus:border-marke focus:ring-3 focus:ring-marke/15';

  return (
    <form onSubmit={e => void absenden(e)} className="text-[0.9375rem]">
      <fieldset className="m-0 mb-4 border-0 p-0">
        <legend className="mb-2 text-sm font-medium text-text">{t('Was lief im Markt besser?', 'What did better in the market?')}</legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {([
            ['kandidat', t('Die geprüfte Fassung', 'The tested version'), fassungsname(lauf.variant) || undefined],
            ['baseline', t('Die Vergleichsfassung', 'The baseline'), undefined],
            ['unentschieden', t('Kein Unterschied', 'No difference'), undefined],
          ] as const).map(([wert, text, zusatz]) => (
            <label key={wert} className={`cursor-pointer rounded-lg border p-3 ${gewinner === wert ? 'border-marke bg-marke/[0.05]' : 'border-linie'}`}>
              <input type="radio" name="gewinner" value={wert} checked={gewinner === wert} onChange={() => setGewinner(wert)} className="mr-2 accent-marke" />
              {text}
              {zusatz && <span className="block text-[0.8125rem] text-text-hell">{t(<>„{zusatz}“</>, <>“{zusatz}”</>)}</span>}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="mb-4 block">
        <span className="mb-1.5 block text-sm font-medium text-text">{t('Wie wurde getestet?', 'How was it tested?')}</span>
        <select value={aufbau} onChange={e => setAufbau(e.target.value as typeof aufbau)} className={eingabe}>
          <option value="" disabled>{t('Bitte wählen', 'Please choose')}</option>
          {AUFBAUTEN.map(x => <option key={x.wert} value={x.wert}>{sprache() === 'en' ? x.en : x.text}</option>)}
        </select>
        <span className="mt-1.5 block text-[0.8125rem] leading-relaxed text-text-hell">
          {t(
            'In die Trefferquote zählen nur Tests mit fester Aufteilung. Melde trotzdem jedes Ergebnis; es bleibt gespeichert.',
            'Only tests with a fixed split count towards the hit rate. Report every result anyway; it stays saved.',
          )}
        </span>
      </label>

      <label className="mb-4 block">
        <span className="mb-1.5 block text-sm font-medium text-text">{t('Woran gemessen', 'Measured by')}</span>
        <select value={metrik} onChange={e => setMetrik(e.target.value as typeof metrik)} className={eingabe}>
          {METRIKEN.map(x => <option key={x.wert} value={x.wert}>{metrikText(x).text}</option>)}
        </select>
      </label>

      <fieldset className="m-0 mb-2 border-0 p-0">
        <legend className="mb-1 text-sm font-medium text-text">{t('Die Zahlen', 'The numbers')} <span className="font-normal text-text-hell">{t('(empfohlen)', '(recommended)')}</span></legend>
        <p className="mt-0 mb-2 text-[0.8125rem] leading-relaxed text-text-hell">
          {t(
            <>Nur mit Zahlen lässt sich prüfen, ob der Unterschied im Markt größer war als der Zufall.
            Ohne sie zählt das Ergebnis, bleibt aber ungeprüft.</>,
            <>Only with numbers can we check whether the difference in the market was bigger than chance.
            Without them the result counts, but stays unchecked.</>,
          )}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {([[t('Geprüfte Fassung', 'Tested version'), 'kz', 'kn'], [t('Vergleichsfassung', 'Baseline'), 'bz', 'bn']] as const).map(([titel, a, b]) => (
            <div key={titel} className="rounded-lg border border-linie-zart p-3">
              <div className="mb-2 text-[0.8125rem] font-medium text-text-leise">{titel}</div>
              <div className="grid grid-cols-2 gap-2">
                <label><span className="mb-1 block text-[0.75rem] text-text-hell">{m.ereignis}</span>
                  <input inputMode="numeric" value={z[a]} onChange={e => setZ({ ...z, [a]: e.target.value })} className={eingabe} /></label>
                <label><span className="mb-1 block text-[0.75rem] text-text-hell">{m.basis}</span>
                  <input inputMode="numeric" value={z[b]} onChange={e => setZ({ ...z, [b]: e.target.value })} className={eingabe} /></label>
              </div>
            </div>
          ))}
        </div>
      </fieldset>

      <label className="mt-3 mb-4 block">
        <span className="mb-1.5 block text-sm font-medium text-text">{t('Zeitraum', 'Period')} <span className="font-normal text-text-hell">(optional)</span></span>
        <input value={zeitraum} onChange={e => setZeitraum(e.target.value)} placeholder={t('z. B. 3 Wochen ab 01.10.', 'e.g. 3 weeks from 1 Oct')} className={eingabe} />
      </label>

      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}

      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-48 flex-1 sm:flex-none"><Knopf type="submit" laedt={laedt}>{t('Ergebnis melden', 'Report the result')}</Knopf></div>
        <KleinerKnopf type="button" onClick={abbrechen}>{t('Abbrechen', 'Cancel')}</KleinerKnopf>
      </div>
      <p className="mt-3 mb-0 text-[0.8125rem] text-text-hell">{t('Kostenlos. Eine neue Meldung zum selben Run ersetzt die alte.', 'Free. A new report for the same run replaces the old one.')}</p>
    </form>
  );
}
